// Main Application Component
import React, { useState, useEffect } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from './config/supabase';
import { normalizeLanguage, getLanguageName } from './config/languages';
import { AI_GENERATION_ENDPOINT, AI_EXPERT_GENERATION_ENDPOINT } from './config/apiEndpoints';
import LoginView from './components/LoginView';
import AuthCallback from './components/AuthCallback';
import { LandingPage } from './components/views/LandingPage';
import { DashboardView } from './components/views/DashboardView';
import { RoadmapCreationForm, type RoadmapFormData } from './components/views/RoadmapCreationForm';
import { RoadmapViewer } from './components/views/RoadmapViewer';
import { ProfileView } from './components/views/ProfileView';
import { ContactPage } from './components/views/ContactPage';
import ExamView from './components/views/ExamView';
import { Header } from './components/Header';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { GlobalLoadingSpinner } from './components/GlobalLoadingSpinner';
import type { RoadmapData, ArticleData } from './types';
import { 
  saveRoadmap, 
  getAllRoadmaps,
  getUserRoadmaps,
  linkRoadmapToUser,
  unlinkRoadmapFromUser,
  checkRoadmapExists,
  recordToRoadmapData,
  getCompletedArticlesForRoadmap,
  debugGetUserArticleProgress
} from './services/roadmapService';
import { 
  saveArticle, 
  getArticleById, 
  recordToArticleData
} from './services/articleService';
import { cacheQuiz } from './services/quizCacheService';
import { fetchVideoIdForArticle } from './services/youtubeService';
import { generateArticleId, generateRoadmapId, generateManualRoadmapId, generateManualArticleId } from './utils/idGenerator';
import { processResources } from './utils/resourceProcessor';
import { useStreak, useSubscription } from './hooks';
import { BillingService } from './services/billingService';
import SignUpView from './components/SignUpView';
import AdminPanel from './components/views/AdminPanel';
import { AdminInbox } from './components/admin/AdminInbox';
import { PricingModal } from './components/ui/PricingModal';
import FloatingTutorButton from './components/ai/FloatingTutorButton';
import ContextualTutorModal from './components/ai/ContextualTutorModal';

type View = 'home' | 'dashboard' | 'create' | 'viewer' | 'profile' | 'contact' | 'login' | 'signup' | 'admin' | 'inbox' | 'auth-callback' | 'exams';

function App() {
  const [currentView, setCurrentView] = useState<View>('home');
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isTutorModalOpen, setIsTutorModalOpen] = useState(false);
  const [isAppLoaded, setIsAppLoaded] = useState(false);
  const { streak, updateStreak } = useStreak(user?.email);
  const { subscription, refreshSubscription } = useSubscription(user?.email);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('theme');
    return (saved as 'light' | 'dark') || 'dark';
  });

  const [roadmaps, setRoadmaps] = useState<RoadmapData[]>([]);

  // Admin detection for roadmap management
  const isAdmin = user?.email === 'dynamicnabid@gmail.com' || user?.email?.includes('@admin');

  const [cachedArticles, setCachedArticles] = useState<Record<string, ArticleData>>({});

  const [activeRoadmap, setActiveRoadmap] = useState<RoadmapData | null>(null);
  const [currentAdminRoadmap, setCurrentAdminRoadmap] = useState<any>(null);
  const [viewedArticle, setViewedArticle] = useState<ArticleData | null>(null);
  const [loadingArticle, setLoadingArticle] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({
    percentage: 0,
    message: '',
    stage: 'idle'
  });

  // Helper to generate storage keys per user (fallback system)
  const storageKeysFor = (email?: string | null) => {
    const safeId = (email || 'guest').replace(/[^a-z0-9]/gi, '_');
    return {
      roadmapsKey: `nanolez_roadmaps_${safeId}`,
      articlesKey: `nanolez_articles_cache_${safeId}`
    };
  };

  // Normalize a user-selected language to code + human-friendly name
  const normalizeLanguagePreference = (lang: string) => {
    const result = normalizeLanguage(lang);
    return { code: result.code, name: result.name };
  };

  // Persist theme
  useEffect(() => {
    localStorage.setItem('theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Handle auth callback route
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token') || hash.includes('type=signup')) {
      console.log('🔐 Detected auth callback in URL, switching to callback view...');
      setCurrentView('auth-callback');
    }
  }, []);

  // Clean up hash on successful auth
  useEffect(() => {
    if (currentView !== 'auth-callback' && !window.location.hash.includes('access_token')) {
      // Auth callback is complete, hash has been cleared
      console.log('✅ Auth callback complete - hash cleared, view changed');
    }
  }, [currentView]);

  // Monitor auth state
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setAuthLoading(false);

      // Redirect authenticated users from home/login to dashboard
      if (currentUser) {
        if (currentView === 'home' || currentView === 'login') {
          setCurrentView('dashboard');
        }
      } else {
        // Redirect unauthenticated users from protected views to home
        if (
          currentView !== 'home' &&
          currentView !== 'login' &&
          currentView !== 'contact' &&
          currentView !== 'signup'
        ) {
          setCurrentView('home');
          setActiveRoadmap(null);
          setViewedArticle(null);
        }
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setAuthLoading(false);
      
      // Mark app as loaded once auth is complete
      setIsAppLoaded(true);

      // Redirect authenticated users from home/login to dashboard
      if (currentUser) {
        if (currentView === 'home' || currentView === 'login') {
          setCurrentView('dashboard');
        }
      } else {
        // Redirect unauthenticated users from protected views to home
        if (
          currentView !== 'home' &&
          currentView !== 'login' &&
          currentView !== 'contact' &&
          currentView !== 'signup'
        ) {
          setCurrentView('home');
          setActiveRoadmap(null);
          setViewedArticle(null);
        }
      }
    });

    return () => subscription?.unsubscribe();
  }, [currentView]);

  // Handle payment result from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    
    if (paymentStatus === 'success') {
      alert('🎉 Welcome to Pro! Your account has been upgraded successfully.');
      refreshSubscription();
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'failed') {
      alert('❌ Payment failed. Please try again or contact support.');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      alert('ℹ️ Payment was cancelled.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [refreshSubscription]);

  // 🔍 Startup diagnostics - log system info and environment
  useEffect(() => {
    console.group('🚀 NanoLez App Startup Diagnostics');
    console.log('⏱️ Timestamp:', new Date().toISOString());
    console.log('🌍 Environment:', {
      isProduction: import.meta.env.PROD,
      isDev: import.meta.env.DEV,
      mode: import.meta.env.MODE,
      apiEndpoint: AI_GENERATION_ENDPOINT
    });
    console.log('💻 Browser:', {
      userAgent: navigator.userAgent,
      language: navigator.language,
      onLine: navigator.onLine
    });
    console.log('🔗 Network:', {
      hostname: window.location.hostname,
      port: window.location.port || 'default',
      protocol: window.location.protocol,
      origin: window.location.origin
    });
    console.log('🔐 Auth:', {
      userLoaded: !authLoading,
      isAuthenticated: !!user,
      userId: user?.id || 'not-authenticated',
      email: user?.email || 'not-authenticated'
    });
    console.log('📚 Roadmaps:', {
      count: roadmaps.length,
      cachedArticles: Object.keys(cachedArticles).length
    });
    console.log('✅ All systems ready for video playback');
    console.log('📺 Video will search when article loads. Check "🎬 VideoPlayer" logs below.');
    console.groupEnd();
  }, [user, roadmaps, cachedArticles, authLoading]);


  // Load roadmaps from Supabase (primary) or localStorage (fallback)
  useEffect(() => {
    // Only clear data if user is not authenticated AND auth has finished loading
    if (!user && authLoading === false) {
      console.warn('⚠️ User logged out, clearing all data');
      setRoadmaps([]);
      setCachedArticles({});
      setActiveRoadmap(null);
      setViewedArticle(null);
      return;
    }

    // Skip if still loading or no user
    if (authLoading || !user?.email) {
      console.log('⏳ Waiting for auth to load or missing user email');
      return;
    }

    const loadRoadmaps = async () => {
      const { roadmapsKey, articlesKey } = storageKeysFor(user?.email || 'guest');
      
      console.group('🔄 Loading Roadmaps & Articles');
      console.log('User Email:', user.email);
      console.log('Storage keys:', { roadmapsKey, articlesKey });
      
      try {
        // Load only user's roadmaps from Supabase (shared universal database with user-specific visibility)
        console.log('📡 Fetching from Supabase for user:', user.email);
        const { data, error } = await getUserRoadmaps(user.email!, 100, 0);
        if (error) {
          console.warn('⚠️  Supabase unavailable, using local cache:', error);
          // Fallback to localStorage
          try {
            const saved = localStorage.getItem(roadmapsKey);
            const parsedRoadmaps = saved ? JSON.parse(saved) : [];
            setRoadmaps(parsedRoadmaps);
            console.log('📦 Roadmaps loaded from localStorage:', parsedRoadmaps.length);
          } catch (e) {
            console.error('❌ Failed to parse localStorage roadmaps:', e);
            setRoadmaps([]);
          }
        } else if (data && data.length > 0) {
          console.log('✅ Roadmaps loaded from Supabase:', data.length);
          setRoadmaps(data);
          // Also save to localStorage as backup
          localStorage.setItem(roadmapsKey, JSON.stringify(data));
          console.log('💾 Roadmaps cached to localStorage');
        } else {
          console.log('ℹ️  No roadmaps found in Supabase, checking localStorage...');
          // Check localStorage
          try {
            const saved = localStorage.getItem(roadmapsKey);
            const parsedRoadmaps = saved ? JSON.parse(saved) : [];
            if (parsedRoadmaps.length > 0) {
              setRoadmaps(parsedRoadmaps);
              console.log('📦 Roadmaps loaded from localStorage:', parsedRoadmaps.length);
            } else {
              console.log('ℹ️  No roadmaps in localStorage either');
              setRoadmaps([]);
            }
          } catch (e) {
            console.error('❌ Failed to parse localStorage roadmaps:', e);
            setRoadmaps([]);
          }
        }
      } catch (error) {
        console.warn('❌ Failed to load from Supabase, trying localStorage:', error);
        // Fallback to localStorage
        try {
          const saved = localStorage.getItem(roadmapsKey);
          const parsedRoadmaps = saved ? JSON.parse(saved) : [];
          setRoadmaps(parsedRoadmaps);
          console.log('📦 Roadmaps loaded from localStorage (fallback):', parsedRoadmaps.length);
        } catch (e) {
          console.error('❌ Failed to parse localStorage roadmaps:', e);
          setRoadmaps([]);
        }
      }

      // Load articles cache from localStorage
      try {
        const saved = localStorage.getItem(articlesKey);
        const parsedArticles = saved ? JSON.parse(saved) : {};
        setCachedArticles(parsedArticles);
        console.log('📚 Articles cache loaded:', Object.keys(parsedArticles).length, 'articles');
        console.log('📝 Article cache keys:', Object.keys(parsedArticles));
        console.log('📦 Cache structure:', Object.entries(parsedArticles).map(([k, v]: any) => ({ 
          key: k, 
          title: v.title, 
          topic: v.topic,
          dayId: v.dayId 
        })));
      } catch (e) {
        console.error('❌ Failed to parse localStorage articles:', e);
        setCachedArticles({});
      }
      
      console.groupEnd();
    };

    loadRoadmaps();
  }, [user?.email, authLoading]);

  // Persist roadmaps to localStorage as fallback backup
  useEffect(() => {
    const { roadmapsKey } = storageKeysFor(user?.email || 'guest');
    try {
      localStorage.setItem(roadmapsKey, JSON.stringify(roadmaps));
      console.log('💾 Roadmaps auto-saved to localStorage');
    } catch (e) {
      console.warn('Failed to save roadmaps to localStorage:', e);
    }
  }, [roadmaps, user]);

  // Persist cached articles to localStorage as fallback backup
  useEffect(() => {
    const { articlesKey } = storageKeysFor(user?.email || 'guest');
    try {
      localStorage.setItem(articlesKey, JSON.stringify(cachedArticles));
      console.log('💾 Articles cache auto-saved to localStorage');
    } catch (e) {
      console.warn('Failed to save articles to localStorage:', e);
    }
  }, [cachedArticles, user]);

  // Handle roadmap creation
  const handleCreateRoadmap = async (formData: RoadmapFormData) => {
    // Quota check
    if (user?.email) {
      const { allowed, reason } = await BillingService.canGenerateRoadmap(user.email, formData.skillLevel);
      if (!allowed) {
        setIsPricingModalOpen(true);
        return;
      }
    }

    setLoading(true);
    setProgress({ percentage: 10, message: 'Analyzing your learning goals...', stage: 'analyzing' });

    try {
      const { code: langCode, name: langName } = normalizeLanguagePreference(formData.language);

      // Define skill level structures with INDUSTRY REQUIREMENTS ANALYSIS
      const skillLevelStructures = {
        'Beginner': {
          description: 'Foundation level - industry fundamentals, core concepts, and complete skill overview',
          instruction: `Create a beginner roadmap analyzing industry requirements for "${formData.goal}":
          
STRUCTURE (4 weeks + 2 review days):
- Weeks 1-4: Core fundamentals, essential concepts, industry-recognized foundations
- Day 29: "Month Review - Part 1: Core Concepts Mastery" - Review all core topics learned
- Day 30: "Month Review - Part 2: Practical Application" - Practice problems and hands-on exercises

Focus on what industry considers ESSENTIAL for entry-level professionals in this field.`,
          duration: '1 month',
          finalDays: ''
        },
        'Intermediate': {
          description: 'Applied level - industry standards, advanced concepts, and professional skills',
          instruction: `Create an intermediate 3-month roadmap analyzing industry requirements for "${formData.goal}":

STRUCTURE (Each month: 4 weeks + 2 review days):
- Weeks 1-4 each month: Intermediate concepts, industry best practices, professional standards
- Day 29 of each month: "Month Review - Part 1: Advanced Concepts & Best Practices" - Review month's techniques
- Day 30 of each month: "Month Review - Part 2: Project-Based Learning" - Mini-projects applying learned concepts

Month 1-2: Intermediate skills and professional practices for this field
Month 3: Career preparation - resume, portfolio, interview skills

Include what industry looks for in intermediate professionals.`,
          duration: '3 months',
          finalDays: 'Month 3 Day 29-30 MUST include: 1) Building professional portfolio, 2) Career opportunities mapping'
        },
        'Expert': {
          description: 'Complete A-Z knowledge progression to junior role readiness in 6 months',
          instruction: `Create a COMPREHENSIVE 6-month EXPERT roadmap for "${formData.goal}" with STRICT PROGRESSIVE STRUCTURE - designed to take learners from ZERO to JUNIOR ROLE READY:

REQUIRED 6-MONTH PROGRESSION (MUST FOLLOW EXACTLY):

MONTH 1 - FOUNDATIONAL MASTERY (Building the Base):
- Complete foundations of the field from absolute beginner level
- Core concepts, theory, fundamental principles, essential terminology
- Basic tools and technologies setup
- All absolute basics to understand what this field is about
- By end of Month 1: Learner understands complete fundamentals
- Week 4 Day 30: Foundational Quiz covering ALL basics (Month 1 Days 1-28)

MONTH 2-3 - INTERMEDIATE LEVEL (Gradual Build-up):
- Build GRADUALLY on Month 1 foundations
- Intermediate tools, frameworks, best practices, industry standards
- Real-world practical implementations and case studies
- Hands-on projects applying intermediate concepts
- Industry patterns and architectural thinking
- By end of Month 3: Learner can build medium-complexity projects independently
- Each week progresses systematically from Month 1 concepts
- Month 3 Day 30: Intermediate Assessment covering Months 1-3 concepts

MONTH 4-5 - ADVANCED EXPERT TOPICS (Project-Heavy):
- Advanced specializations BUILDING ON intermediate knowledge
- Cutting-edge technologies, complex problem-solving
- MOSTLY PROJECT-BASED - 70% hands-on projects, 30% theory
- Performance optimization, scalability, security at expert level
- Real business problem solving with architectural decisions
- System design and complex integrations
- By end of Month 5: Portfolio with 3-4 production-quality projects
- Month 5 Day 30: Advanced Problem-Solving Assessment

MONTH 6 - CAREER & JOB PREPARATION (Job-Ready):
- Career positioning for junior/entry-level professional roles
- Resume building, portfolio optimization, LinkedIn profile
- Technical interview preparation (DSA, design patterns, common questions)
- Behavioral interview training and soft skills
- Salary negotiation, job search strategies
- Day 1-7: Resume and portfolio building
- Day 8-14: Technical interview prep (coding problems, system design)
- Day 15-21: Soft skills and behavioral interviews
- Day 22-28: Final project showcase and career positioning
- Day 29: Mock interview practice
- Day 30: Career Launch Checklist - verify readiness for junior roles

A-Z COVERAGE (ENSURE COMPLETE JUNIOR-LEVEL KNOWLEDGE):
By end of Month 6, learner MUST know:
1. ALL foundational concepts from absolute beginner
2. Complete toolchain and technology stack for the field
3. Industry best practices and standards
4. Real-world application development end-to-end
5. How to build medium to advanced projects independently
6. Common patterns, antipatterns, and optimization techniques
7. Version control, deployment, and production concerns
8. Debugging and problem-solving methodologies
9. Security and performance considerations
10. How to land first junior role and succeed in it

MONTH-BY-MONTH STRUCTURE:
- Weeks 1-4 (Days 1-28): Daily topics with specific, unique content
  * Days 1-6 each week: Theory and concepts appropriate to that month's level
  * Day 7 each week: Hands-on project (Month 1-2: educational, Month 4-5: portfolio-building)
- Day 29 & 30: Month review and assessment appropriate to that month

IMPORTANT FORMATTING CONSTRAINTS:
- topic (day title): MAXIMUM 50 characters. Must be a concise title WITHOUT description
- task (short description): MAXIMUM 150 characters. Only include what will be learned/built that day
- NO detailed explanations in topic or task fields
- Keep both topic and task concise and focused

PROGRESSION RULES:
- NO JUMPING: Month 2 MUST build on Month 1, Month 3 on Months 1-2, etc.
- Month 1: All absolute basics, foundations, setup
- Month 2: Build intermediate skills gradually from Month 1
- Month 3: Advanced intermediate, complex real-world scenarios
- Month 4-5: Advanced topics, cutting-edge, 70% project-based
- Month 6: Career-focused, interview prep, job readiness
- Each day builds on previous days logically and systematically
- NO scattered random topics - STRICT SEQUENTIAL PROGRESSION

ENSURE:
- Content flows naturally day-by-day without jumping levels
- By Month 3 Day 30, learner can build real projects independently
- By Month 5 Day 30, learner has 3-4 production-quality portfolio projects
- Month 6 guarantees junior-level job-readiness for entry positions
- ALL topics are COMPREHENSIVE with examples and real-world context
- LATEST 2024-2025 technologies and industry practices`,
          duration: '6 months',
          finalDays: 'Month 6 Day 29-30 MUST cover: 1) Mock interview practice, 2) Resume and portfolio final review, 3) Job search strategies, 4) First role expectations, 5) Salary negotiation for junior positions'
        }
      };

      const levelConfig = skillLevelStructures[formData.skillLevel as keyof typeof skillLevelStructures] || skillLevelStructures['Beginner'];

      const systemPrompt = `You are an expert in the "${formData.goal}" field. 

CRITICAL LANGUAGE REQUIREMENT:
- Generate ALL roadmap content (topic names, task descriptions, project titles) in ${langName}
- The entire roadmap MUST be written in ${langName} language
- This is a ${langName}-speaking user, so every single word should be in ${langName}

Analyze INDUSTRY REQUIREMENTS and generate COMPLETE learning roadmaps with EVERY SINGLE DAY specified, including the review days at end of each month. Output ONLY valid JSON with no markdown.`;
      
      // TWO-STEP GENERATION: Split 6 months into 2 parts to avoid truncation
      setProgress({ percentage: 40, message: 'Analyzing industry requirements and generating months 1-3...', stage: 'building' });

      // STEP 1: Generate first 3 months with industry requirements focus
      const prompt1 = `${levelConfig.instruction}

${formData.skillLevel === 'Beginner' ? `Generate 1 month of roadmap:
MONTH 1 STRUCTURE (30 days):
- Week 1-4 (Days 1-28): Core fundamentals covering INDUSTRY-ESSENTIAL knowledge for beginners
  * Day 7 of each week: "Week Project" - Hands-on project applying that week's concepts
  * Days 1-6: Teach theory, concepts, and fundamentals
- Day 29: "Monthly Overview - Foundation Mastery Review" - Comprehensive summary and review of all core topics learned in days 1-28
- Day 30: "Monthly Quiz - Beginner Assessment" - Comprehensive quiz/assessment covering ALL topics from days 1-28, including multiple choice, short answer, and hands-on exercises

IMPORTANT FORMATTING CONSTRAINTS:
- topic (day title): MAXIMUM 50 characters. Must be a concise title WITHOUT description or extra context
- task (short description): MAXIMUM 150 characters. Only include what will be discussed/learned in this day
- NO detailed explanations in topic or task fields
- Keep both topic and task concise and focused

Return: {title, months:[{name, weeks:[{name, goal, days:[{day:1-7, topic:specific_topic, task:actionable_task}]}], finalReview:[{day:29, topic, task}, {day:30, topic, task}]}]}
CRITICAL: Day 7 of each week MUST be a hands-on project. Days 29-30 MUST be in a separate "finalReview" array.
REMEMBER: Write ALL content (topics, tasks, project names) in ${langName}. The user speaks ${langName}.
Make sure EVERY day (including Day 29 overview and Day 30 quiz) has unique, specific content in ${langName}.` : formData.skillLevel === 'Intermediate' ? `Generate months 1-3 of roadmap for INTERMEDIATE level:
EACH MONTH STRUCTURE (30 days):
- Week 1-4 (Days 1-28): Intermediate concepts, industry best practices, professional standards
  * Day 7 of each week: "Week Project" - Hands-on project applying that week's techniques and best practices
  * Days 1-6: Teach intermediate concepts and industry patterns
- Day 29: "Monthly Overview - Techniques & Best Practices Review" - Comprehensive review of all techniques, patterns, and best practices from days 1-28
- Day 30: "Monthly Quiz - Intermediate Assessment" - Comprehensive quiz covering ALL topics from days 1-28, including scenario-based questions, coding problems, and real-world applications

Month 1-2: Intermediate skills aligned with industry expectations
Month 3: Career preparation (Resume, Portfolio, Interview skills)

IMPORTANT FORMATTING CONSTRAINTS:
- topic (day title): MAXIMUM 50 characters. Must be a concise title WITHOUT description or extra context
- task (short description): MAXIMUM 150 characters. Only include what will be discussed/learned in this day
- NO detailed explanations in topic or task fields
- Keep both topic and task concise and focused

Return: {title, months:[{name, weeks:[{name, goal, days:[{day:1-7, topic:specific_topic, task:actionable_task}]}], finalReview:[{day:29, topic, task}, {day:30, topic, task}]}]}
CRITICAL: Day 7 of each week MUST be a hands-on project. Days 29-30 MUST be in a separate "finalReview" array at end of each month.
REMEMBER: Write ALL content (topics, tasks, project names) in ${langName}. The user speaks ${langName}.
Make sure EVERY day (including Day 29 overview and Day 30 quiz) has content in ${langName}.` : `Generate months 1-3 of EXPERT level roadmap with STRICT PROGRESSIVE STRUCTURE:

MONTH 1 - FOUNDATIONAL MASTERY:
- All absolute basics, core concepts, fundamental principles, terminology
- Basic tool setup and environment
- Week 1-4 covers complete beginner foundations before any intermediate content
- Day 29: Foundation Review - recap all basics
- Day 30: Foundational Quiz - test complete understanding of Month 1 (Days 1-28)

MONTH 2 - EARLY INTERMEDIATE (Building on Month 1):
- Build DIRECTLY on Month 1 foundations - NO jumping ahead
- Intermediate tools, frameworks, best practices
- Real-world applications using Month 1 foundations + Month 2 concepts
- Day 29: Intermediate Review - recap Month 1 + Month 2 together
- Day 30: Intermediate Assessment - test Months 1-2 knowledge

MONTH 3 - ADVANCED INTERMEDIATE (Complete Project Capability):
- Advanced intermediate topics building on Months 1-2
- Complex real-world scenarios and architectural thinking
- By Day 28: Learner can build medium-complexity projects independently
- Week 4 Day 7: Capstone project showcasing Months 1-3 knowledge
- Day 29: Mastery Review - comprehensive Month 1-3 review
- Day 30: Assessment - verify learner ready for Month 4 advanced topics

DAILY PROGRESSION RULES (MUST FOLLOW):
- Day 1-6 Week 1: Absolute basics of Month 1 topic
- Day 7 Week 1: Hands-on project using Week 1 basics
- Day 1-6 Week 2: Building on Week 1 + new basics
- Day 7 Week 2: Hands-on project using Weeks 1-2 concepts
- Continue this GRADUAL progression through Week 4
- NO jumping topics - each day builds on previous
- MONTH 2: Reference Month 1 concepts in everything
- MONTH 3: Reference Months 1-2 concepts in everything

IMPORTANT FORMATTING CONSTRAINTS:
- topic (day title): MAXIMUM 50 characters. Must be a concise title WITHOUT description
- task (short description): MAXIMUM 150 characters. Only include what will be learned/built
- NO detailed explanations in topic or task fields
- Keep both topic and task concise and focused

Return: {title, months:[{name, weeks:[{name, goal, days:[{day:1-7, topic:specific_topic, task:actionable_task}]}], finalReview:[{day:29, topic, task}, {day:30, topic, task}]}]}
CRITICAL: Day 7 of each week MUST be a hands-on project. Days 29-30 MUST be in separate "finalReview" array.
REMEMBER: Write ALL content in ${langName}. The user speaks ${langName}.
Make sure EVERY day (1-30 per month) has unique, specific content in ${langName}.
ENSURE content flows naturally without jumping levels - STRICT SEQUENTIAL PROGRESSION.`}`;

      // ROUTE TO APPROPRIATE API: Expert uses Google Gemini, others use Mistral
      const isExpert = formData.skillLevel === 'Expert';
      const apiEndpoint = isExpert ? AI_EXPERT_GENERATION_ENDPOINT : AI_GENERATION_ENDPOINT;
      
      let response1;
      try {
        response1 = await fetch(apiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: prompt1,
            systemPrompt,
            jsonMode: true,
            useGoogleAPI: isExpert,
            fallbackToMistral: true
          })
        });

        // If Expert endpoint fails with 404, try regular endpoint (fallback during dev)
        if (!response1.ok && isExpert && response1.status === 404) {
          console.warn('⚠️  Expert endpoint (generate-expert) returned 404. Falling back to regular endpoint...');
          console.warn('💡 Tip: If this is local dev, restart your dev server with: npm run dev');
          response1 = await fetch(AI_GENERATION_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              prompt: prompt1,
              systemPrompt,
              jsonMode: true,
              useGoogleAPI: false,
              fallbackToMistral: true
            })
          });
        }

        if (!response1.ok) throw new Error(`API returned ${response1.status}: ${response1.statusText}`);
      } catch (fetchError) {
        console.error('❌ Fetch error:', fetchError);
        throw new Error(`Failed to connect to roadmap generation API: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
      }

      const result1 = await response1.json();
      let roadmapPart1 = result1.data || result1;
      
      if (typeof roadmapPart1 === 'string') {
        try {
          let cleanJson = roadmapPart1.trim();
          if (cleanJson.startsWith('```')) {
            cleanJson = cleanJson.replace(/^```(?:json)?[\s\n]*/i, '').replace(/[\s\n]*```$/i, '').trim();
          }
          roadmapPart1 = JSON.parse(cleanJson);
        } catch (e) {
          console.error('❌ JSON Parse Error (Part 1):', {
            originalLength: roadmapPart1.length,
            error: e instanceof Error ? e.message : String(e)
          });
          throw new Error(`Failed to parse first part of roadmap: ${e instanceof Error ? e.message : 'Invalid JSON'}`);
        }
      }

      // STEP 2: Generate months 4-6 (only for Intermediate and Expert - Beginner is 1 month only)
      let roadmapPart2: any = { months: [] };
      
      if (formData.skillLevel !== 'Beginner') {
        setProgress({ percentage: 60, message: `Analyzing industry requirements and generating months ${formData.skillLevel === 'Intermediate' ? '4' : '4-6'}...`, stage: 'building' });

        const prompt2 = `${levelConfig.instruction}

${formData.skillLevel === 'Intermediate' ? `Generate month 3 (final month) for INTERMEDIATE level roadmap:
MONTH 3 STRUCTURE (30 days):
- Week 1-4 (Days 1-28): Career preparation - Resume, Portfolio, Interview prep
  * Day 7 of each week: "Week Project" - Hands-on project (portfolio piece, mock interview, resume refinement)
  * Days 1-6: Career preparation content
- Day 29: "Monthly Overview - Career Readiness Review" - Comprehensive review of portfolio, resume, and professional brand built in days 1-28
- Day 30: "Monthly Quiz - Career Assessment" - Comprehensive quiz covering job search strategies, interview techniques, and career planning from days 1-28

IMPORTANT FORMATTING CONSTRAINTS:
- topic (day title): MAXIMUM 50 characters. Must be a concise title WITHOUT description or extra context
- task (short description): MAXIMUM 150 characters. Only include what will be discussed/learned in this day
- NO detailed explanations in topic or task fields
- Keep both topic and task concise and focused

Return: {months:[{name, weeks:[{name, goal, days:[{day:1-7, topic:specific_topic, task:actionable_task}]}], finalReview:[{day:29, topic, task}, {day:30, topic, task}]}]}
CRITICAL: Day 7 of each week MUST be a hands-on project. Days 29-30 MUST be in a separate "finalReview" array.
${levelConfig.finalDays}
Make sure EVERY day (1-30) has content in ${langName}.` : `Generate months 4-6 of EXPERT level roadmap with STRICT PROGRESSION:

MONTH 4 - ADVANCED SPECIALIZATIONS (Building on Months 1-3):
- Advanced specializations DIRECTLY building on Months 1-3 knowledge
- Cutting-edge technologies and techniques
- 70% hands-on projects, 30% theory
- Week 1-4 Day 7: Portfolio-quality projects
- Day 29: Advanced Review - how Month 4 builds on Months 1-3
- Day 30: Advanced Assessment - test Month 4 + ability to apply Months 1-3

MONTH 5 - EXPERT LEVEL & COMPLEX SYSTEMS (Project-Heavy):
- Expert-level topics building on Months 1-4
- MOSTLY PROJECT-BASED (70% projects, 30% theory)
- Performance optimization, scalability, security
- Real business problem solving
- System design and complex integrations
- Week 1-4 Day 7: Major portfolio projects
- By Day 28: 3-4 production-quality portfolio projects
- Day 29: Expert Mastery Review - complete understanding of Months 1-5
- Day 30: Expert Assessment - advanced problem-solving, design thinking

MONTH 6 - CAREER & JOB READINESS (Junior Role Preparation):
- Career positioning for junior/entry-level professional roles
- Resume & portfolio optimization based on Months 1-5 projects
- Technical interview prep using Months 1-5 knowledge
- Day 1-7: Resume, portfolio, LinkedIn optimization
- Day 8-14: Coding interview prep (DSA, coding problems)
- Day 15-21: System design interviews, soft skills training
- Day 22-28: Final project showcase, career positioning, mock interviews
- Day 29: Mock interview practice - full interview simulation
- Day 30: Career Launch Checklist - verify junior-role readiness

IMPORTANT FORMATTING CONSTRAINTS:
- topic (day title): MAXIMUM 50 characters. Must be a concise title WITHOUT description
- task (short description): MAXIMUM 150 characters. Only include what will be learned/built
- NO detailed explanations in topic or task fields
- Keep both topic and task concise and focused

VIDEO CONTENT FOR MONTHS 4-6:
- Prioritize HIGH-DURATION, IN-DEPTH videos (45-120+ minutes)
- Include research presentations, expert interviews, conference talks, technical deep-dives
- Focus on cutting-edge practices, emerging technologies, system design
- Prefer content for solving real problems, architectural decisions
- Reference authoritative resources from industry leaders

Return: {months:[{name, weeks:[{name, goal, days:[{day:1-7, topic:specific_topic, task:detailed_task}]}], finalReview:[{day:29, topic, task}, {day:30, topic, task}]}]}

CRITICAL: Day 7 of each week MUST be substantial hands-on project. Days 29-30 in separate "finalReview" array.
${levelConfig.finalDays}
Write ALL content in ${langName}.
ENSURE Month 4-5 projects are portfolio-building quality, Month 6 is career-focused, and by Month 6 Day 30 learner is ready for junior-level jobs.`}`;

        let response2;
        try {
          response2 = await fetch(apiEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              prompt: prompt2,
              systemPrompt,
              jsonMode: true,
              useGoogleAPI: isExpert,
              fallbackToMistral: true
            })
          });

          // If Expert endpoint fails with 404, try regular endpoint (fallback during dev)
          if (!response2.ok && isExpert && response2.status === 404) {
            console.warn('⚠️  Expert endpoint returned 404 on part 2. Using regular endpoint fallback...');
            response2 = await fetch(AI_GENERATION_ENDPOINT, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                prompt: prompt2,
                systemPrompt,
                jsonMode: true,
                useGoogleAPI: false,
                fallbackToMistral: true
              })
            });
          }

          if (!response2.ok) throw new Error(`API returned ${response2.status}: ${response2.statusText}`);
        } catch (fetchError) {
          console.error('❌ Fetch error (part 2):', fetchError);
          throw new Error(`Failed to generate second part: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
        }

        const result2 = await response2.json();
        roadmapPart2 = result2.data || result2;
        
        if (typeof roadmapPart2 === 'string') {
          try {
            let cleanJson = roadmapPart2.trim();
            if (cleanJson.startsWith('```')) {
              cleanJson = cleanJson.replace(/^```(?:json)?[\s\n]*/i, '').replace(/[\s\n]*```$/i, '').trim();
            }
            roadmapPart2 = JSON.parse(cleanJson);
          } catch (e) {
            console.error('❌ JSON Parse Error (Part 2):', {
              originalLength: roadmapPart2.length,
              error: e instanceof Error ? e.message : String(e)
            });
            throw new Error(`Failed to parse second part of roadmap: ${e instanceof Error ? e.message : 'Invalid JSON'}`);
          }
        }
      }

      // STEP 3: Combine both parts
      setProgress({ percentage: 75, message: 'Combining roadmap sections...', stage: 'assembling' });

      const roadmapData = {
        title: roadmapPart1.title || `${formData.goal} - ${formData.skillLevel} Roadmap`,
        months: [
          ...(roadmapPart1.months || []),
          ...(roadmapPart2.months || [])
        ]
      };
      setProgress({ percentage: 88, message: 'Polishing your roadmap...', stage: 'finalizing' });
      
      const newRoadmap: RoadmapData = {
        ...roadmapData,
        goal: formData.goal,
        completedDays: [],
        language: langCode,
        lang: langCode,
        intensity: formData.skillLevel, // Store skill level as intensity for backwards compatibility
        meta: {
          goal: formData.goal,
          duration: levelConfig.duration,
          studyTime: 'Self-paced'
        },
        created_at: new Date().toISOString()
      };

      setProgress({ percentage: 95, message: 'Saving to database...', stage: 'saving' });
      
      // Try to save to Supabase (primary)
      let savedToSupabase = false;
      try {
        const { data: savedRoadmap, error: saveError, isNew } = await saveRoadmap(
          newRoadmap,
          user?.id
        );

        if (saveError) {
          console.warn('⚠️  Failed to save roadmap to Supabase:', saveError);
        } else if (savedRoadmap) {
          savedToSupabase = true;
          
          // Assign the structured ID
          newRoadmap.id = savedRoadmap.id;
          
          // Link roadmap to user (tracks that user created this roadmap)
          if (user) {
            try {
              await linkRoadmapToUser(user.email!, savedRoadmap.id, 'created');
              if (isNew) {
                console.log('✅ New roadmap saved and linked to user');
                // Record generation for quota tracking
                await BillingService.recordGeneration(user.email!);
              } else {
                console.log('♻️  Roadmap already exists, reusing shared version and linking to user');
              }
            } catch (linkError) {
              console.warn('⚠️  Failed to link roadmap to user:', linkError);
              // Non-critical error - roadmap is saved, user just won't see it in their dashboard
            }
          }
        }
      } catch (error) {
        console.warn('Failed to save roadmap to Supabase:', error);
      }

      if (!savedToSupabase) {
        // Generate ID locally for offline usage - use manual roadmap ID format
        newRoadmap.id = generateManualRoadmapId(newRoadmap.title || newRoadmap.goal || 'roadmap');
        console.log('💾 Roadmap saved to local storage (offline)');
      }

      setProgress({ percentage: 100, message: 'Your roadmap is ready!', stage: 'complete' });
      setRoadmaps([newRoadmap, ...roadmaps]);
      
      // Explicitly save to localStorage for fallback
      const { roadmapsKey } = storageKeysFor(user?.email || 'guest');
      try {
        const updated = [newRoadmap, ...roadmaps];
        localStorage.setItem(roadmapsKey, JSON.stringify(updated));
        console.log('💾 Roadmap persisted to localStorage');
      } catch (e) {
        console.warn('Failed to persist roadmap to localStorage:', e);
      }
      
      setActiveRoadmap(newRoadmap);
      setTimeout(() => {
        setCurrentView('viewer');
        setProgress({ percentage: 0, message: '', stage: 'idle' });
        setLoading(false);
      }, 900);

    } catch (error) {
      console.error('Error generating roadmap:', error);
      alert('Failed to generate roadmap. Please try again.');
      setProgress({ percentage: 0, message: '', stage: 'idle' });
      setLoading(false);
    }
  };

  // Handle fetching article for a day
  const handleFetchArticle = async (day: any) => {
    if (!activeRoadmap) return;

    // Calculate the actual sequential day number across the entire roadmap
    let sequentialDayNumber = 1;
    let foundDay = false;
    
    outerLoop:
    for (const month of activeRoadmap.months || []) {
      for (const week of month.weeks || []) {
        for (const d of week.days || []) {
          if (d.topic === day.topic && d.task === day.task) {
            foundDay = true;
            break outerLoop;
          }
          sequentialDayNumber++;
        }
      }
    }
    
    if (!foundDay) {
      console.warn('⚠️ Could not find day in roadmap, using fallback');
      sequentialDayNumber = day.day || 1;
    }

    // Generate roadmapId first, then articleId using the roadmapId and day number
    const roadmapId = generateRoadmapId(
      activeRoadmap.goal || 'general',
      activeRoadmap.intensity || 'beginner',
      activeRoadmap.language || 'en'
    );
    
    const articleId = generateArticleId(roadmapId, sequentialDayNumber);
    const cacheKey = articleId;
    const dayId = articleId;

    console.group('📖 Fetching Article');
    console.log('📋 Article Identifiers:', {
      topic: day.topic,
      task: day.task,
      goal: activeRoadmap.goal,
      language: activeRoadmap.language,
      articleId,
      cacheKey,
      dayId
    });
    console.log('🗃️ Cache Status:', {
      totalCached: Object.keys(cachedArticles).length,
      isInCache: cachedArticles[cacheKey] !== undefined,
      availableKeys: Object.keys(cachedArticles).slice(0, 5) // Show first 5 for brevity
    });

    // Check if article is already in memory cache
    if (cachedArticles[cacheKey]) {
      console.log('✅ Article loaded from memory cache:', dayId);
      console.log('Article data:', {
        title: cachedArticles[cacheKey].title,
        hasDeepDive: !!cachedArticles[cacheKey].deepDive,
        hasSteps: !!(cachedArticles[cacheKey].steps?.length),
        hasQuiz: !!cachedArticles[cacheKey].quiz
      });
      console.groupEnd();
      
      // Ensure quiz is in cache if article has it
      if (cachedArticles[cacheKey].quiz) {
        try {
          await cacheQuiz(
            articleId,
            cachedArticles[cacheKey].title,
            cachedArticles[cacheKey].quiz,
            activeRoadmap?.id,
            activeRoadmap?.goal
          );
        } catch (quizCacheError) {
          console.warn('⚠️ Failed to cache quiz:', quizCacheError);
        }
      }
      
      setViewedArticle({ ...cachedArticles[cacheKey], articleSource: 'memory' });
      return;
    }
    
    console.log('❌ Article NOT in memory cache, will fetch...');

    setLoadingArticle(dayId);

    try {
      // Check if article exists in Supabase first (using unified key)
      console.log('🔍 Checking Supabase for article:', articleId);
      
      let existingArticle = null;
      try {
        const { data, error: fetchError } = await getArticleById(articleId);
        if (data && !fetchError) {
          existingArticle = data;
          console.log('✅ Found article in Supabase');
        } else if (fetchError) {
          console.warn('⚠️ Supabase lookup failed:', fetchError);
        } else {
          console.log('ℹ️ Article not in Supabase, will generate with AI');
        }
      } catch (supabaseError) {
        console.error('❌ Supabase connection error:', supabaseError);
        console.log('💡 Proceeding with AI generation (offline)');
      }
      
      if (existingArticle) {
        console.log('♻️ Article loaded from Supabase (shared):', articleId);
        const articleData = {
          ...recordToArticleData(existingArticle),
          dayId,
          cachedAt: new Date().toISOString(),
          articleSource: 'database' as const
        };
        
        // Cache quiz if it exists
        if (articleData.quiz) {
          try {
            await cacheQuiz(
              articleId,
              articleData.title,
              articleData.quiz,
              activeRoadmap?.id,
              activeRoadmap?.goal
            );
            console.log('✅ Quiz cached from database article');
          } catch (quizCacheError) {
            console.warn('⚠️ Failed to cache quiz:', quizCacheError);
          }
        }
        
        // Cache in memory and update state synchronously
        const updatedCache = { ...cachedArticles, [cacheKey]: articleData };
        setCachedArticles(updatedCache);
        setViewedArticle(articleData);
        setLoadingArticle(null);
        console.log('✅ Article cached and ready (from database)');
        console.log('📦 Cache updated with key:', cacheKey);
        console.groupEnd();
        return;
      }

      // Article doesn't exist, generate with AI
      console.log('🤖 Generating article with AI...');
      const userLanguage = activeRoadmap.language || activeRoadmap.lang || 'en';
      const languageName = normalizeLanguagePreference(userLanguage).name;
      
      const systemPrompt = `You are an expert educational content writer. Generate articles in VALID JSON format ONLY.

⚠️ CRITICAL JSON REQUIREMENTS:
- MUST return VALID, parseable JSON (no syntax errors)
- ALL strings MUST have special characters properly escaped:
  - Quotes must be escaped: "text with \"quotes\"" 
  - Newlines must be escaped: "line 1\\nline 2"
  - Backslashes must be escaped: "path\\\\to\\\\file"
- NO markdown code blocks (no triple backticks)
- NO extra text before or after the JSON object
- Return ONLY the JSON object, nothing else

CONTENT REQUIREMENTS:
- Write SPECIFICALLY about "${day.topic}", not general topics
- All content in ${languageName} (EXCEPT searchTerm which is ALWAYS in English)
- Make it deep, focused, and practical
- Include working code/real examples appropriate to the topic

JSON SCHEMA:
{
  "title": "topic title in language",
  "subtitle": "topic description in language",
  "deepDive": "detailed explanation with examples in language - PLAIN TEXT ONLY, NO MARKDOWN, NO FORMATTING, escape special characters properly",
  "technicalConcepts": [{"term": "english term", "explanation": "explanation in language - plain text only"}],
  "steps": ["step 1 in language - plain text", "step 2 in language - plain text"],
  "example": "code or metaphor in language - plain text only, NO MARKDOWN CODE BLOCKS, escape special characters",
  "exampleType": "code or metaphor",
  "keywords": ["keyword1", "keyword2", "keyword3", "keyword4"],
  "searchTerm": "ALWAYS in ENGLISH ONLY - never translate to ${languageName}",
  "quiz": {
    "questions": [
      {
        "id": "q1",
        "question": "question in ${languageName}",
        "type": "multiple-choice or true-false",
        "options": ["option1", "option2", "option3", "option4"],
        "correctAnswer": 0,
        "explanation": "why this answer is correct in ${languageName}",
        "difficulty": "easy or medium or hard",
        "concept": "core concept being tested in English"
      }
    ],
    "totalQuestions": 10,
    "passingScore": 70,
    "timeLimit": 900
  },
  "resources": {"articles": [{"title": "Title", "url": "https://..."}], "video": {}}
}

QUIZ GENERATION RULES (MANDATORY):
- Generate EXACTLY 10 multiple-choice or true-false questions
- Mix of 6-7 multiple-choice (4 options each) and 3-4 true-false (2 options)
- Focus on INTERVIEW PREPARATION and CORE CONCEPT UNDERSTANDING
- Vary difficulty: 3 easy, 4 medium, 3 hard questions
- Each question tests critical knowledge from the article
- Correct answer index: 0-based (0, 1, 2, or 3 for multiple-choice; 0 or 1 for true-false)
- Provide brief, clear explanations for why each answer is correct
- Questions should help professionals pass technical interviews
- Format options as plain text strings in an array of exactly 4 (or 2 for true-false)
- Use clear, professional language appropriate to ${languageName}
- Ensure variety: some test definitions, some test applications, some test analysis
- totalQuestions: MUST be 10
- passingScore: MUST be 70
- timeLimit: 900 seconds (15 minutes) MUST be exact

CRITICAL - SEARCH TERM MUST BE ENGLISH:
- searchTerm MUST ALWAYS be in ENGLISH
- DO NOT TRANSLATE searchTerm to ${languageName}
- searchTerm is used for YouTube search - must be in English for effectiveness
- Example: Even if language is Spanish, searchTerm stays in English like "REST API design in spanish" not "diseño API REST en spanish"

MANDATORY - SEARCH TERM RULES (SEO EXPERT MODE):
- Role: You are an expert SEO search term creator optimizing for YouTube discovery
- searchTerm format: 
  * If language is English: "[1-4 ENGLISH keywords]" (NO language suffix)
  * If language is NOT English: "[1-4 ENGLISH keywords] in ${languageName}"
- Extract ONLY the most relevant, searchable ENGLISH keywords from title and subtitle
- Prioritize high-impact words that appear first in title/subtitle
- NEVER exceed 4 words - be concise and targeted
- NEVER translate these words to ${languageName}
- NEVER include filler words like "the", "a", "how to", "learn", "tutorial"
- Focus on: core concepts, technical terms, primary subject matter
- searchTerm field is REQUIRED - never skip it
- All content fields required and non-empty
- technicalConcepts: English terms only, explanations in ${languageName}`;

      const prompt = `Create an educational article on: "${day.topic}"
Task: ${day.task}
Language: ${languageName}
Goal/Domain: ${activeRoadmap.goal}
Context: Part of "${activeRoadmap.goal}" learning path

FOCUS:
- Deep, specific explanation of "${day.topic}"
- Not a general overview of "${activeRoadmap.goal}"
- Practical examples and technical concepts
- All content in ${languageName}

🔴 CRITICAL - SEARCH TERM MUST BE ENGLISH - DO NOT TRANSLATE:

The searchTerm field is ONLY for YouTube search and MUST be in ENGLISH.
Even though this article is in ${languageName}, the searchTerm words must STAY IN ENGLISH.
Keep the words in their original English form as they appear in title/subtitle.

WRONG - DO NOT DO THIS:
  If title is "Advanced REST API Design" and language is Spanish:
  searchTerm: "diseño API REST avanzado en español" ❌ (translated words - WRONG)
  
RIGHT - DO THIS:
  If title is "Advanced REST API Design" and language is Spanish:
  searchTerm: "REST API design advanced in spanish" ✅ (English words kept + language suffix)

⚠️ CRITICAL SEARCH TERM GENERATION (SEO EXPERT):

As an expert SEO search term creator, generate search terms optimized for YouTube discovery.
Format: [1-4 KEYWORDS FROM TITLE] + [GOAL KEY IF NOT DUPLICATE] + " in ${languageName}"

🔴 STRICT ALGORITHM (FOLLOW EXACTLY):

STEP 1: Extract Keywords (1-4 words from title/subtitle)
- Pick the most searchable, specific words from title and subtitle
- Exclude filler: "the", "a", "an", "and", "or", "is", "are", "how", "to", "learn", "guide", "tutorial", "introduction"
- Choose words that best represent the article concept
- Keep them in logical order (most important first)

STEP 2: Identify Goal Key
- Get the goal/domain keyword: "${activeRoadmap.goal}"
- This is the domain/framework context (e.g., "React", "Python", "Node.js", "PostgreSQL")
- Use this EXACT value when checking for duplication and adding to searchTerm

STEP 3: Check for Duplication
- Look at keywords extracted in STEP 1
- IF goal key already appears in keywords → SKIP adding it again (avoid "React React state management")
- IF goal key NOT in keywords → ADD it at the BEGINNING before other keywords

STEP 4: Add Language Prefix
- Append " in ${languageName}" at the end

STEP 5: Validate & Be Specific
- Total 3-5 words (4-word target)
- All words MUST be searchable and specific
- NO generic phrases
- ALWAYS keep high specificity for better YouTube results

EXACT EXAMPLES (FOLLOW THIS PATTERN):

✅ EXAMPLE 1 - Language is ENGLISH (NO language suffix):
   Title: "Array Sorting Algorithms"
   Subtitle: "Comparing bubble sort, quick sort implementations"
   Goal: "Python"
   Language: "English"
   Keywords extracted: ["array", "sorting", "algorithms"]
   Goal "Python" NOT in keywords → ADD it
   searchTerm: "Python array sorting algorithms" ✓ (NO "in english" suffix)

✅ EXAMPLE 2 - Language is SPANISH (add language suffix):
   Title: "React State Management Hooks"
   Subtitle: "Managing component state with custom hooks"
   Goal: "React"
   Language: "Spanish"
   Keywords extracted: ["React", "state", "management", "hooks"]
   Goal "React" ALREADY in keywords → SKIP adding it again
   searchTerm: "React state management hooks in spanish" ✓ (WITH language suffix)

✅ EXAMPLE 3 - Language is ENGLISH (NO language suffix):
   Title: "Binary Search Implementation"
   Subtitle: "Efficient searching in sorted arrays"
   Goal: "JavaScript"
   Language: "English"
   Keywords extracted: ["binary", "search", "implementation"]
   Goal "JavaScript" NOT in keywords → ADD it
   searchTerm: "JavaScript binary search implementation" ✓ (NO language suffix for English)

✅ EXAMPLE 4 - Language is FRENCH (add language suffix):
   Title: "REST API Design Principles"
   Subtitle: "Building scalable web services"
   Goal: "Node.js"
   Language: "French"
   Keywords extracted: ["REST", "API", "design", "principles"]
   Goal "Node.js" NOT in keywords → ADD it
   searchTerm: "Node.js REST API design principles in french" ✓ (WITH language suffix)

✅ EXAMPLE 5 - Language is HINDI (add language suffix):
   Title: "Database Query Optimization"
   Subtitle: "Improving SQL performance with indexing"
   Goal: "PostgreSQL"
   Language: "Hindi"
   Keywords extracted: ["database", "query", "optimization"]
   Goal "PostgreSQL" NOT in keywords → ADD it
   searchTerm: "PostgreSQL database query optimization in hindi" ✓ (WITH language suffix)

❌ WRONG EXAMPLES (DO NOT DO):
   ❌ "React React state management" (goal key duplicated when already in keywords)
   ❌ "React state management in english" (added "in english" when language IS English)
   ❌ "Python array algorithms advanced concepts searching" (exceeds 4 words)
   ❌ "how to implement array sorting in python" (has filler words)

❌ "state management React in english" (goal key in middle = awkward)
❌ "React React state management in english" (goal key duplicated)
❌ "state management in english" (missing goal key when it should be included)
❌ "React state management middleware hooks authentication in english" (too many words, not focused)
❌ "array sorting algorithms how to implement in english" (filler words included)

REMEMBER: Be SPECIFIC, avoid duplication, ALWAYS include goal key first if not already present!

Return ONLY valid JSON with all required fields. No markdown.`;

      const response = await fetch(AI_GENERATION_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: day.topic,
          prompt,
          systemPrompt,
          jsonMode: true
        })
      });

      if (!response.ok) {
        let errorMsg = 'Failed to load article';
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorMsg;
        } catch {
          errorMsg = `Server error: ${response.status} ${response.statusText}`;
        }
        console.error('❌ AI Response Error:', {
          status: response.status,
          statusText: response.statusText,
          message: errorMsg
        });
        throw new Error(errorMsg);
      }

      const result = await response.json();
      console.log('📥 AI Response received');
      
      let apiData = result.data || result;
      
      // Remove ANSI color codes from response
      // Parse article JSON using robust parser
      if (typeof apiData === 'string') {
        const { parseJSONObject } = await import('@/utils/jsonParser');
        
        const parseResult = parseJSONObject<any>(
          apiData,
          'Article Content',
          ['title', 'subtitle', 'searchTerm'] // Required fields for articles
        );

        if (!parseResult.success) {
          console.error('❌ Article JSON parse failed:', parseResult.error);
          console.error('Strategy attempted:', parseResult.strategy);
          console.error('First 500 chars:', apiData.substring(0, 500));
          throw new Error(`Failed to parse article JSON: ${parseResult.error}`);
        }

        apiData = parseResult.data;
        console.log(`✅ Article parsed successfully using strategy: ${parseResult.strategy}`);
      }

      // CRITICAL: Validate searchTerm is present
      if (!apiData?.searchTerm) {
        console.error('❌ CRITICAL: AI did not generate searchTerm');
        console.error('Article:', { title: apiData?.title, subtitle: apiData?.subtitle });
        throw new Error('AI failed to generate searchTerm. Article generation invalid.');
      } else {
        console.log('✅ SearchTerm validated:', apiData.searchTerm);
      }

      if (apiData?.resources) {
        // Process and optimize resources (max 3 articles, PDFs prioritized)
        const originalCount = apiData.resources.articles?.length || 0;
        apiData.resources = processResources(apiData.resources);
        const processedCount = apiData.resources.articles?.length || 0;
        if (originalCount !== processedCount) {
          console.log(`Resources processed: ${originalCount} -> ${processedCount} (max 3, PDFs prioritized)`);
        }
      }
      
      console.log('📝 Article structure:', {
        hasTitle: !!apiData?.title,
        hasSubtitle: !!apiData?.subtitle,
        hasDeepDive: !!apiData?.deepDive,
        conceptCount: apiData?.technicalConcepts?.length || 0,
        stepCount: apiData?.steps?.length || 0,
        hasResources: !!apiData?.resources,
        hasVideo: !!apiData?.resources?.video,
        articleCount: apiData?.resources?.articles?.length || 0,
        searchTerm: apiData?.searchTerm || '❌ MISSING - Videos cannot be fetched',
        hasSearchTerm: !!apiData?.searchTerm
      });
      
      // Validate critical field
      if (!apiData?.searchTerm) {
        console.warn('⚠️ WARNING: AI did not generate searchTerm. Video search will fail.');
        console.warn('🔧 Check AI prompt to ensure searchTerm field is required in JSON schema.');
      }
      
      // Fetch YouTube video if searchTerm exists
      let videoData = apiData?.resources?.video || {};
      if (apiData?.searchTerm) {
        try {
          // Truncate search term to maximum 6 words (excluding language suffix)
          let searchTermToUse = apiData.searchTerm;
          const languageMatch = searchTermToUse.match(/\s+in\s+(.+)$/i);
          const languageSuffix = languageMatch ? languageMatch[0] : ''; // " in [language]"
          const mainSearchTerm = searchTermToUse.replace(/\s+in\s+.+$/i, '').trim();
          
          // Split by spaces and limit to 6 words
          const words = mainSearchTerm.split(/\s+/);
          if (words.length > 6) {
            const truncatedTerms = words.slice(0, 6).join(' ');
            searchTermToUse = truncatedTerms + languageSuffix;
            console.log(`⚠️  Search term truncated from ${words.length} to 6 words:`);
            console.log(`   Original: "${apiData.searchTerm}"`);
            console.log(`   Truncated: "${searchTermToUse}"`);
          }
          
          console.log('🎬 Fetching video with optimized search term:', searchTermToUse);
          const result = await fetchVideoIdForArticle(
            searchTermToUse,
            activeRoadmap.language || activeRoadmap.lang || 'en',
            day.topic // Pass topic for fallback searches
          );
          
          if (result && result.videoId && result.videos) {
            const { videoId, videos } = result;
            videoData = {
              id: videoId, // Default to conceptual (primary)
              popular: videos.popular,
              conceptual: videos.conceptual,
              recent: videos.recent,
              url: `https://www.youtube.com/watch?v=${videoId}`,
              embedUrl: `https://www.youtube.com/embed/${videoId}`,
              searchTerm: searchTermToUse
            };
            console.log('✅ Videos fetched successfully:', {
              primary: videoId,
              popular: videos.popular,
              conceptual: videos.conceptual,
              recent: videos.recent
            });
          } else {
            console.warn('⚠️ No videos found for search term:', searchTermToUse, result?.error);
          }
        } catch (err) {
          console.error('❌ Video fetch error:', err);
        }
      }
      
      const articleData: ArticleData = {
        ...(apiData || {}),
        id: articleId, // Include the generated ID
        dayId,
        topic: day.topic,
        task: day.task,
        goal: activeRoadmap.goal, // Include goal for proper caching
        language: activeRoadmap.language || 'en',
        roadmapId: activeRoadmap.id,
        resources: {
          articles: apiData?.resources?.articles || [], // AI-provided resource links
          video: videoData // Auto-populated YouTube video
        },
        cachedAt: new Date().toISOString(),
        articleSource: 'generated' as const
      };

      console.log('💾 Attempting to save article with unified caching...');
      console.log('📝 Article Data:', {
        id: articleData.id,
        title: articleData.title,
        goal: articleData.goal,
        dayId: articleData.dayId,
        topic: articleData.topic,
        cacheKey: cacheKey,
        language: articleData.language
      });

      // Always save to memory cache and localStorage FIRST (most reliable)
      const updatedCache = {
        ...cachedArticles,
        [cacheKey]: articleData // Use consistent cache key
      };
      setCachedArticles(updatedCache);
      
      // Explicitly persist to localStorage
      const { articlesKey } = storageKeysFor(user?.email || 'guest');
      try {
        localStorage.setItem(articlesKey, JSON.stringify(updatedCache));
        console.log('✅ Article saved to localStorage with cache key:', cacheKey);
        console.log('📦 Total cached articles:', Object.keys(updatedCache).length);
      } catch (e) {
        console.error('❌ Failed to persist article to localStorage:', e);
        alert('⚠️ Could not save to local storage. Your article may be lost if you refresh.');
      }

      // Try to save to Supabase (for universal sharing and caching)
      let savedToSupabase = false;
      try {
        console.log('🔄 Attempting Supabase save for universal caching...');
        console.log('📤 Sending article data to database with all fields:', {
          title: articleData.title,
          topic: articleData.topic,
          language: articleData.language,
          deepDiveLength: articleData.deepDive?.length,
          technicalConceptsCount: articleData.technicalConcepts?.length,
          stepsCount: articleData.steps?.length,
          hasResources: !!articleData.resources
        });

        const { data: savedArticle, error: saveError, isNew } = await saveArticle(
          articleData,
          activeRoadmap.id,
          user?.id
        );

        if (saveError) {
          console.warn('⚠️ Supabase save failed (article still cached locally):', saveError);
          console.log('💡 Article is saved in memory and localStorage');
          console.log('❌ Database persistence FAILED - article NOT in Supabase');
        } else if (savedArticle) {
          savedToSupabase = true;
          console.log('✅ Article persisted to database successfully with all fields');
          console.log('🗄️ Database record contains:', {
            id: savedArticle.id,
            title: savedArticle.title,
            topic: savedArticle.topic,
            language: savedArticle.language,
            deepDiveLength: savedArticle.deep_dive?.length || 0,
            technicalConceptsCount: savedArticle.technical_concepts?.length || 0,
            stepsCount: savedArticle.steps?.length || 0,
            hasVideo: !!savedArticle.resources?.video?.id
          });
          
          if (isNew) {
            console.log('✅ Article saved to Supabase for universal sharing');
            console.log('🌍 This article can now be shared across users');
          } else {
            console.log('♻️ Article already exists in Supabase (shared resource)');
          }
        }
      } catch (error) {
        console.warn('⚠️ Supabase connection issue (article cached locally):', error);
        console.log('💡 You can continue - article is in memory and localStorage');
        console.log('❌ Database persistence FAILED - article NOT saved to Supabase');
      }

      // Cache quiz data if it exists
      if (articleData.quiz && articleData.id) {
        try {
          await cacheQuiz(
            articleData.id,
            articleData.title,
            articleData.quiz,
            activeRoadmap.id,
            activeRoadmap.goal
          );
          console.log('✅ Quiz cached successfully for article:', articleData.id);
        } catch (quizCacheError) {
          console.warn('⚠️ Failed to cache quiz (article still works):', quizCacheError);
        }
      }

      setViewedArticle(articleData);
      console.log('✅ Article ready and displayed!');
      console.log('📺 VideoPlayer will now search for videos');
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to load article';
      console.error('❌ Error fetching article:', {
        message: errorMsg,
        error: error,
        stack: error instanceof Error ? error.stack : undefined
      });
      alert(`Failed to load article: ${errorMsg}. Check console for details (F12 > Console).`);
    } finally {
      setLoadingArticle(null);
      console.groupEnd();
    }
  };

  // Handle toggling day completion
  const handleToggleDay = (dayId: string) => {
    if (!activeRoadmap) return;

    const updatedRoadmaps = roadmaps.map(roadmap => {
      if (roadmap.id === activeRoadmap.id) {
        const completedDays = roadmap.completedDays.includes(dayId)
          ? roadmap.completedDays.filter(id => id !== dayId)
          : [...roadmap.completedDays, dayId];
        
        const updated = { ...roadmap, completedDays };
        setActiveRoadmap(updated);
        return updated;
      }
      return roadmap;
    });

    setRoadmaps(updatedRoadmaps);
  };

  // Find current day and next day in roadmap
  const findCurrentAndNextDay = () => {
    if (!activeRoadmap || !activeRoadmap.months || !viewedArticle?.dayId) {
      console.warn('⚠️ Cannot find next day - missing roadmap or dayId');
      return { currentDay: null, nextDay: null };
    }

    let foundCurrent = false;
    let currentDay = null;
    let nextDay = null;
    let sequentialDayNumber = 1;

    try {
      for (const month of activeRoadmap.months) {
        if (!month.weeks) continue;
        for (const week of month.weeks) {
          if (!week.days) continue;
          for (const day of week.days) {
            // Generate roadmapId first, then articleId
            const roadmapId = generateRoadmapId(
              activeRoadmap.goal || 'general',
              activeRoadmap.intensity || 'beginner',
              activeRoadmap.language || 'en'
            );
            const dayId = generateArticleId(roadmapId, sequentialDayNumber);
            
            if (dayId === viewedArticle.id) {
              foundCurrent = true;
              currentDay = day;
              console.log('✅ Found current day:', day.topic);
            } else if (foundCurrent && !nextDay) {
              nextDay = day;
              console.log('✅ Found next day:', day.topic);
              return { currentDay, nextDay };
            }
            
            sequentialDayNumber++;
          }
        }
      }
    } catch (error) {
      console.error('❌ Error in findCurrentAndNextDay:', error);
    }

    console.warn('⚠️ No next day found, current day:', currentDay?.topic);
    return { currentDay, nextDay };
  };

  // Handle complete day
  const handleCompleteDay = async (dayId: string) => {
    if (!activeRoadmap || !user?.email) return;

    try {
      console.group('✅ Completing Day & Article');
      console.log('Day ID:', dayId);
      
      // Mark as completed locally
      const completedDays = activeRoadmap.completedDays.includes(dayId)
        ? activeRoadmap.completedDays
        : [...activeRoadmap.completedDays, dayId];

      const updatedRoadmap = { ...activeRoadmap, completedDays };
      setActiveRoadmap(updatedRoadmap);

      // Update in roadmaps list
      const updatedRoadmaps = roadmaps.map(r => r.id === activeRoadmap.id ? updatedRoadmap : r);
      setRoadmaps(updatedRoadmaps);

      // Save to localStorage
      const { roadmapsKey } = storageKeysFor(user?.email || 'guest');
      try {
        localStorage.setItem(roadmapsKey, JSON.stringify(updatedRoadmaps));
        console.log('✅ Day marked as completed and saved locally');
      } catch (e) {
        console.warn('Failed to save to localStorage:', e);
      }

      // Try to update in Supabase
      try {
        await supabase
          .from('roadmaps')
          .update({ completed_days: completedDays })
          .eq('id', activeRoadmap.id);
        console.log('✅ Day completion saved to Supabase');

        // Also mark the article as completed in user_article_progress
        try {
          const { data: existing, error: queryError } = await supabase
            .from('user_article_progress')
            .select('id')
            .eq('email', user.email)
            .eq('article_id', dayId)
            .maybeSingle();

          if (queryError) {
            console.error('❌ Query error when fetching existing progress:', queryError);
          }

          if (existing) {
            // Update existing record
            const { error: updateError } = await supabase
              .from('user_article_progress')
              .update({
                status: 'completed',
                progress_percentage: 100,
                last_accessed_at: new Date().toISOString(),
                completed_at: new Date().toISOString()
              })
              .eq('id', existing.id);
            
            if (updateError) {
              console.error('❌ Error updating article progress:', updateError);
            } else {
              console.log('✅ Article progress updated to completed');
            }
          } else {
            // Create new record
            const { error: insertError } = await supabase
              .from('user_article_progress')
              .insert({
                email: user.email,
                user_id: user.id,
                article_id: dayId,
                roadmap_id: activeRoadmap.id,
                status: 'completed',
                progress_percentage: 100,
                started_at: new Date().toISOString(),
                last_accessed_at: new Date().toISOString(),
                completed_at: new Date().toISOString()
              });
            
            if (insertError) {
              console.error('❌ Error creating article progress:', insertError);
            } else {
              console.log('✅ Article progress created as completed');
            }
          }
        } catch (articleError) {
          console.error('❌ Exception while saving article progress:', articleError);
        }

        // Update learning streak
        await updateStreak();
        console.groupEnd();
      } catch (error) {
        console.warn('⚠️ Could not update Supabase (will sync on next load):', error);
        console.groupEnd();
      }
    } catch (error) {
      console.error('Error completing day:', error);
    }
  };

  // Handle load next day article
  const handleLoadNextDay = async () => {
    if (!activeRoadmap) {
      console.error('❌ No active roadmap to load next day');
      return;
    }

    console.group('📖 Loading Next Day');
    const { currentDay, nextDay } = findCurrentAndNextDay();
    console.log('Current day:', currentDay?.topic);
    console.log('Next day:', nextDay?.topic);
    console.groupEnd();
    
    if (!nextDay) {
      console.warn('⚠️ No next day available');
      alert('🎉 You have completed this week! Great progress!');
      return;
    }

    // Fetch the next day's article
    console.log('🚀 Fetching article for next day:', nextDay.topic);
    await handleFetchArticle(nextDay);
  };

  // Handle selecting a roadmap
  const handleSelectRoadmap = (roadmap: RoadmapData) => {
    console.group('🎯 Selecting Roadmap');
    console.log('Roadmap ID:', roadmap.id);
    console.log('Roadmap Title:', roadmap.title);
    console.log('Roadmap Goal:', roadmap.goal);
    console.log('Has months?', !!roadmap.months);
    console.log('Month count:', roadmap.months?.length || 0);
    console.log('Completed days:', roadmap.completedDays?.length || 0);
    console.groupEnd();
    
    setActiveRoadmap(roadmap);
    setViewedArticle(null);
    setCurrentView('viewer');
    
    // Link roadmap to user (tracks that user viewed this roadmap)
    if (user && roadmap.id) {
      linkRoadmapToUser(user.email!, roadmap.id, 'viewed')
        .catch(error => console.warn('Failed to link roadmap as viewed:', error));
    }
  };

  // Load completed days when roadmap is selected
  useEffect(() => {
    if (!activeRoadmap || !user?.email) {
      console.log('⏭️  Skipping completed days load - no active roadmap or user');
      return;
    }

    const loadCompletedDays = async () => {
      try {
        console.group('📊 Loading Completed Days');
        console.log('Roadmap ID:', activeRoadmap.id);
        console.log('User Email:', user.email);
        
        // Check if roadmap has an ID before proceeding
        if (!activeRoadmap.id) {
          console.log('⚠️  Roadmap has no ID, skipping completion check');
          console.groupEnd();
          return;
        }
        
        // DEBUG: Check what's in the user_article_progress table
        await debugGetUserArticleProgress(user.email!);
        
        const { completedArticleIds, error } = await getCompletedArticlesForRoadmap(
          user.email!,
          activeRoadmap.id
        );

        if (error) {
          console.warn('⚠️  Failed to load completed articles:', error);
          console.groupEnd();
          return;
        }

        // Filter to only include articles that are actually part of this roadmap
        // Map article IDs to their day IDs in the roadmap structure
        const completedDayIds: string[] = [];
        
        console.log('=== MAPPING COMPLETED ARTICLES TO DAYS ===');
        console.log('Completed articles from DB:', completedArticleIds);
        console.log('Number of completed articles:', completedArticleIds?.length || 0);
        
        if (activeRoadmap.months && completedArticleIds && completedArticleIds.length > 0 && activeRoadmap.id) {
          let sequentialDayNumber = 1;
          const roadmapId = generateRoadmapId(
            activeRoadmap.goal || 'general',
            activeRoadmap.intensity || 'beginner',
            activeRoadmap.language || 'en'
          );
          
          console.log('=== ID COMPARISON DEBUG ===');
          console.log('activeRoadmap.id (from state):', activeRoadmap.id);
          console.log('Generated roadmapId:', roadmapId);
          console.log('Are they the same?', activeRoadmap.id === roadmapId);
          console.log('Completed article IDs:', completedArticleIds);

          for (const month of activeRoadmap.months) {
            for (const week of month.weeks || []) {
              for (const day of week.days || []) {
                const dayId = generateArticleId(roadmapId, sequentialDayNumber);
                const isCompleted = (completedArticleIds as string[]).includes(dayId);
                
                if (sequentialDayNumber <= 3 || isCompleted) {
                  console.log(`Day ${sequentialDayNumber}: Generated ID = ${dayId}, Completed = ${isCompleted}`);
                }
                
                if (isCompleted) {
                  completedDayIds.push(dayId);
                }
                sequentialDayNumber++;
              }
            }
          }
        } else {
          console.log('⚠️  Cannot map - missing months or no completed articles');
        }

        console.log(`=== MAPPING COMPLETE ===`);
        console.log(`Mapped ${completedDayIds.length} completed days from ${completedArticleIds?.length || 0} articles`);
        
        // Update activeRoadmap with completed days
        if (completedDayIds.length > 0) {
          const updatedRoadmap = {
            ...activeRoadmap,
            completedDays: completedDayIds
          };
          setActiveRoadmap(updatedRoadmap);
          
          // Also update in roadmaps list
          setRoadmaps(prevRoadmaps =>
            prevRoadmaps.map(r =>
              r.id === activeRoadmap.id
                ? updatedRoadmap
                : r
            )
          );
          
          console.log('✅ Completed days loaded and UI updated');
        } else {
          console.log('ℹ️  No completed days found for this roadmap');
        }
        
        console.groupEnd();
      } catch (error) {
        console.error('❌ Error loading completed days:', error);
      }
    };

    loadCompletedDays();
  }, [activeRoadmap?.id, user?.email]);

  // Handle deleting a roadmap from user's dashboard (not from database)
  const handleDeleteRoadmap = async (id: string) => {
    if (confirm('Are you sure you want to remove this roadmap from your dashboard?')) {
      try {
        // Only remove from user's dashboard, not from database
        if (user?.email) {
          await unlinkRoadmapFromUser(user.email, id);
        }
        
        // Update local state
        setRoadmaps(roadmaps.filter(r => r.id !== id));
        if (activeRoadmap?.id === id) {
          setActiveRoadmap(null);
          setCurrentView('dashboard');
        }
        
        console.log('✅ Roadmap removed from your dashboard');
      } catch (error) {
        console.error('Failed to remove roadmap from dashboard:', error);
        alert('Failed to remove roadmap from dashboard. Please try again.');
      }
    }
  };

  // Handle going back to dashboard
  const handleBackToDashboard = () => {
    setViewedArticle(null);
    setActiveRoadmap(null);
    setCurrentView('dashboard');
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setCurrentView('dashboard');
      setRoadmaps([]);
      setCachedArticles({});
      setActiveRoadmap(null);
      setViewedArticle(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const isDark = theme === 'dark';

  return (
    <div className={`fixed inset-0 flex flex-col transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950' 
        : 'bg-gradient-to-br from-zinc-50 via-white to-zinc-50'
    }`}>
      {/* Global Loading Spinner - Shows on initial page load */}
      <GlobalLoadingSpinner 
        isLoading={!isAppLoaded}
        onLoadComplete={() => console.log('✅ App has fully loaded')}
      />

      {/* Full-Screen Loading Overlay for Article Fetching */}
      {loadingArticle && (
        <LoadingSpinner 
          context="article" 
          message="Generating your article..." 
          size="xl"
          fullScreen={true}
        />
      )}
      
      {/* Header */}
      <Header 
        isDark={theme === 'dark'} 
        onThemeToggle={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        currentView={currentView}
        onNavigate={(view: any) => setCurrentView(view)}
        user={user}
        onSignOut={handleSignOut}
        onUpgradeClick={() => setIsPricingModalOpen(true)}
        streakCount={streak?.streak_count}
        isPro={subscription.isPro}
        hasPendingRequest={subscription.hasPendingRequest}
        proExpiresAt={subscription.proExpiresAt}
        onSubscriptionRefresh={refreshSubscription}
      />

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 pb-24 md:pb-12">
        {/* Landing page for unauthenticated users */}
        {!user && currentView === 'home' && (
          <LandingPage 
            theme={theme}
            onGetStarted={() => setCurrentView('signup')}
            onNavigateToContact={() => setCurrentView('contact')}
          />
        )}
        {/* Auth callback handler for OAuth redirects */}
        {currentView === 'auth-callback' && (
          <AuthCallback
            isDark={theme === 'dark'}
            onAuthSuccess={(authUser: User) => {
              setUser(authUser);
              setCurrentView('dashboard');
            }}
          />
        )}
        {/* Sign up view for unauthenticated users */}
        {!user && currentView === 'signup' && (
          <SignUpView 
            isDark={theme === 'dark'}
            onAuthSuccess={(authUser) => {
              setUser(authUser);
              setCurrentView('dashboard');
            }}
            onShowLogin={() => setCurrentView('login')}
          />
        )}
        {/* Login view for unauthenticated users */}
        {!user && currentView === 'login' && (
          <LoginView 
            isDark={theme === 'dark'}
            onAuthSuccess={(authUser: User) => {
              setUser(authUser);
              setCurrentView('dashboard');
            }}
            onShowSignUp={() => setCurrentView('signup')}
          />
        )}

        {/* Dashboard and other views for authenticated users */}
        {user && currentView === 'dashboard' && (
          <DashboardView
            roadmaps={roadmaps}
            onCreateNew={() => setCurrentView('create')}
            onSelectRoadmap={handleSelectRoadmap}
            onSelectAdminRoadmap={setCurrentAdminRoadmap}
            onDeleteRoadmap={handleDeleteRoadmap}
            theme={theme}
            streakCount={streak?.streak_count || 0}
            userEmail={user?.email}
            onUpgradeClick={() => setIsPricingModalOpen(true)}
          />
        )}

        {currentView === 'create' && (
          <RoadmapCreationForm
            onSubmit={handleCreateRoadmap}
            loading={loading}
            progress={progress}
            theme={theme}
            isPro={subscription.isPro}
            onUpgradeClick={() => setIsPricingModalOpen(true)}
            userEmail={user?.email}
            streakCount={streak?.streak_count || 0}
          />
        )}

        {currentView === 'viewer' && activeRoadmap && (
          <RoadmapViewer
            roadmap={activeRoadmap}
            viewedArticle={viewedArticle}
            loadingArticle={loadingArticle}
            cachedArticles={cachedArticles}
            adminRoadmapData={currentAdminRoadmap}
            onBack={handleBackToDashboard}
            onFetchArticle={handleFetchArticle}
            onToggleDay={handleToggleDay}
            onViewArticle={(article: ArticleData) => setViewedArticle(article)}
            onCompleteDay={handleCompleteDay}
            onLoadNextDay={handleLoadNextDay}
            theme={theme}
            user={user}
            isPro={subscription.isPro}
            onUpgradeClick={() => setIsPricingModalOpen(true)}
            roadmapStartDate={activeRoadmap.created_at || null}
          />
        )}

        {currentView === 'contact' && (
          <ContactPage theme={theme} />
        )}

        {user && currentView === 'exams' && (
          <ExamView 
            user={user} 
            theme={theme}
            isPro={subscription.isPro}
            onUpgradeClick={() => {
              setIsPricingModalOpen(true);
              setCurrentView('dashboard');
            }}
          />
        )}

        {currentView === 'profile' && user && (
          <ProfileView
            user={user}
            roadmaps={roadmaps}
            theme={theme}
            onSignOut={handleSignOut}
            onBack={() => setCurrentView('dashboard')}
            onViewArticle={(article: ArticleData | null) => setViewedArticle(article)}
          />
        )}

        {/* Admin panel for admin user */}
        {user && user.email === 'dynamicnabid@gmail.com' && currentView === 'admin' && (
          <AdminPanel 
            user={user} 
            onRefreshSubscription={refreshSubscription}
            theme={theme}
          />
        )}

        {/* Admin inbox for viewing contact messages */}
        {user && user.email === 'dynamicnabid@gmail.com' && currentView === 'inbox' && (
          <div className="animate-in fade-in">
            <AdminInbox theme={theme} />
          </div>
        )}
      </main>

      <PricingModal 
        isOpen={isPricingModalOpen} 
        onClose={() => setIsPricingModalOpen(false)} 
        isDark={theme === 'dark'} 
        user={user}
        onRefreshSubscription={refreshSubscription}
        hasPendingRequest={subscription.hasPendingRequest}
      />

      {(() => {
        const showButton = user && !['login', 'signup', 'home', 'auth-callback'].includes(currentView);
        console.log('[TutorButton Debug] User:', user ? user.email : 'null', 'Current View:', currentView, 'Show Button:', showButton);
        if (showButton) {
          return <FloatingTutorButton onClick={() => setIsTutorModalOpen(true)} />;
        }
        return null;
      })()}

      <ContextualTutorModal 
        isOpen={isTutorModalOpen}
        onClose={() => setIsTutorModalOpen(false)}
        user={user}
        roadmaps={roadmaps}
        articles={cachedArticles}
        isDark={theme === 'dark'}
        isPro={subscription.isPro}
        onUpgradeClick={() => setIsPricingModalOpen(true)}
      />
    </div>
  );
}

export default App;
